import {Button, Pagination, Tooltip, Empty, Checkbox, Grid} from 'antd';
import {Table, Switch, Space} from 'antd';
import {useEffect, useMemo, useRef, useState} from "react";
import {v4} from "uuid";
import {useDataGrid} from "./DataGridContext";
import {useTranslation} from "react-i18next";
import {ClearOutlined, CloseOutlined, EyeOutlined, ReloadOutlined, UnorderedListOutlined} from "@ant-design/icons";
import {useModal} from "../../context/ModalContext";
import ColumnFilter from "../ColumnFilter";
import {isMobile} from "react-device-detect";
import {createVirtualBody} from "./VirtualBody";


const SORT = {
    NONE: -1,
    ASC: 0,
    DESC: 1
}

// Fallback width for columns that don't declare one, used only in virtual mode
// (fixed layout requires a fully-sized colgroup for header/body to align).
const DEFAULT_VIRTUAL_COL_WIDTH = 150;

export const RenderableExpandRow = ({children, expanded, recordId}) => {

    const {t} = useTranslation();
    const {renderItem, renderExpandedRow, refreshDataGrid} = useDataGrid()
    const [item, setItem] = useState('');

    useEffect(() => {
        if (renderItem && renderItem.recordId === recordId) {
            setItem(renderItem)
        }
    }, [renderItem])

    return (
        <>
            {
                expanded && (
                    <div key={item.key}>
                        {/*<Tooltip placement={'bottomLeft'} title={t('Refresh')}>*/}
                        {/*    <Button onClick={() => {*/}
                        {/*        renderExpandedRow(recordId)*/}
                        {/*    }}>*/}
                        {/*        <ReloadOutlined/>*/}
                        {/*    </Button>*/}
                        {/*</Tooltip>*/}
                        {children}
                    </div>
                )
            }
        </>
    )
}

export default function DataGrid({title, scale=0.8}) {
    const {t} = useTranslation();
    const {
        resultModel,
        dataGridProps,
        setSelectedRows,
        onFilterPageSort,
        tableId,
        selectedRows,
        queryModel,
        focusOut,
        refreshDataGrid
    } = useDataGrid()
    const {openModal, closeModal} = useModal()
    const screens = Grid.useBreakpoint();
    const isNarrowViewport = !screens.md; // < 768px
    const [rowSelection, setRowSelection] = useState({
        selectedRowKeys: selectedRows.map(x => x[dataGridProps.rowKey || 'id']),
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRows(selectedRows)
        },
        onSelect: (record, selected, selectedRows) => {

        },
        onSelectAll: (selected, selectedRows, changeRows) => {
        }
    })

    useEffect(() => {
        if (selectedRows) {
            setRowSelection({
                ...rowSelection,
                selectedRowKeys: selectedRows.map(x => x[dataGridProps.rowKey || 'id']),
            })
        }
    }, [selectedRows])

    // ---- Row virtualization (opt-in per grid via setDataGridProps({virtual: true})) ----
    // Only the visible rows are mounted, so a 10k-row page costs about as much as
    // a 20-row one. Everything below is inert unless `virtual` is on: a non-virtual
    // grid keeps rendering exactly as before.
    const virtual = !!dataGridProps.virtual;

    // Measured content width of the table body, used to scale columns to fill the
    // screen (fixed layout would otherwise leave a dead gap on the right).
    const tableWrapRef = useRef(null);
    const [availableWidth, setAvailableWidth] = useState(0);

    useEffect(() => {
        if (!virtual) return;
        const wrap = tableWrapRef.current;
        if (!wrap || typeof ResizeObserver === 'undefined') return;

        const measure = () => {
            const body = wrap.querySelector('.ant-table-body');
            const w = body ? body.clientWidth : wrap.clientWidth;
            if (w) setAvailableWidth(prev => (prev === w ? prev : w));
        };

        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(wrap);
        window.addEventListener('resize', measure);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [virtual, resultModel.data]);

    const virtualComponents = useMemo(
        () => virtual
            ? {body: {wrapper: createVirtualBody(dataGridProps.virtualRowHeight || 39)}}
            : undefined,
        [virtual, dataGridProps.virtualRowHeight]
    );

    // Virtualization needs a fixed layout with EVERY column sized, else the header
    // and body compute widths differently and misalign. Give any width-less column
    // a sane default so a grid can flip on `virtual` without auditing its columns.
    // Non-virtual grids get the untouched original array (same object identity as
    // before, so the in-place sortOrder mutation in onChange still shows up).
    const selWidth = dataGridProps.enableRowSelection ? 40 : 0;
    const virtualColumns = useMemo(() => {
        const cols = dataGridProps.columns || [];
        if (!virtual) return cols;

        const sized = cols.map(col => (
            col.width ? col : {...col, width: DEFAULT_VIRTUAL_COL_WIDTH}
        ));
        if (!availableWidth) return sized;

        // Treat the entered widths as ratios: on a screen wider than their sum,
        // scale every column up proportionally so the table fills the width. On
        // narrower screens keep the entered widths (horizontal scroll).
        const baseSum = sized.reduce((s, c) => s + c.width, 0);
        const dataTarget = availableWidth - selWidth;
        if (baseSum <= 0 || dataTarget <= baseSum) return sized;

        const scale = dataTarget / baseSum;
        const scaled = sized.map(c => ({...c, width: Math.floor(c.width * scale)}));

        // Give the rounding leftover to the last column so it fills exactly.
        const used = scaled.reduce((s, c) => s + c.width, 0);
        const leftover = dataTarget - used;
        if (leftover > 0 && scaled.length) {
            const last = scaled[scaled.length - 1];
            scaled[scaled.length - 1] = {...last, width: last.width + leftover};
        }
        return scaled;
    }, [virtual, dataGridProps.columns, availableWidth, selWidth]);

    // In virtual mode DataGrid owns the table sizing: a complete-width colgroup, a
    // matching scroll.x, fixed layout, and a scroll.y (from the caller or a sane
    // default) that creates the scroll container we virtualize inside. Pulling
    // scroll/tableLayout out of antdTableProps keeps the caller from overriding
    // them via the spread below. Non-virtual behaviour is unchanged.
    const {scroll: callerScroll, tableLayout: callerLayout, ...restAntdProps} = dataGridProps.antdTableProps || {};
    const mergedScroll = useMemo(() => {
        if (!virtual) return callerScroll || {x: 1280, y: window.innerHeight - 300};
        const totalWidth = selWidth + virtualColumns.reduce((sum, c) => sum + (c.width || DEFAULT_VIRTUAL_COL_WIDTH), 0);
        return {x: totalWidth, y: callerScroll?.y ?? (window.innerHeight - 300)};
    }, [virtual, callerScroll, virtualColumns, selWidth]);

    // useEffect(() => {
    //     if (queryModel.orderByItems?.length === 0) {
    //         setColumns([...columns.map(x => {
    //             x.sortOrder = null
    //             return x
    //         })])
    //     }
    // }, [queryModel.updateKey])

    const getSortType = (sort) => {
        if (!sort)
            return SORT.NONE;
        else if (sort === 'ascend')
            return SORT.ASC
        else
            return SORT.DESC
    }

    const onChange = (pagination, filters, sorter) => {
        const _columns = [...dataGridProps.columns].map(x => {
            x.sortOrder = ''
            return x;
        })

        const orderByItems = []
        const col = _columns.find(x => x.key === sorter.field);
        if (sorter.order && col) {
            col.sortOrder = sorter.order
            orderByItems.push({
                name: col.columnName || sorter.field,
                operation: getSortType(sorter.order)
            })
        } else {
            if(col) {
                col.sortOrder = null
            }
        }

        const paginationQuery = {
            pageNumber: pagination.current,
            pageSize: pagination.pageSize
        };

        onFilterPageSort({
            filter: queryModel.filter,
            pagination: paginationQuery,
            orderByItems
        })
    }

    // useEffect(() => {
    //     const table = document.getElementById(`${tableId}_parent`)
    //     if(table) {
    //         table.style.transform = `scale(${scale})`;
    //         table.style.transformOrigin = 'top left';
    //         table.style.width = `${(window.innerWidth * (1/scale))-120}px`;
    //     }
    // }, [scale]);

    const useCardView = (isMobile || isNarrowViewport) && typeof dataGridProps.cardRender === 'function';
    const rowKeyName = dataGridProps.rowKey || 'id';

    const isRowSelected = (record) => {
        return selectedRows.some(r => r[rowKeyName] === record[rowKeyName]);
    };

    const toggleRowSelect = (record) => {
        const selected = isRowSelected(record);
        if (selected) {
            setSelectedRows(selectedRows.filter(r => r[rowKeyName] !== record[rowKeyName]));
        } else {
            setSelectedRows([...selectedRows, record]);
        }
    };

    const renderCardToolbar = () => (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            padding: '8px 4px',
            borderBottom: '1px solid #f0f0f0',
            marginBottom: 8
        }}>
            <Button size="middle" icon={<ReloadOutlined />} onClick={() => refreshDataGrid()} />
            {selectedRows?.length > 0 && (
                <>
                    <Button size="middle" icon={<CloseOutlined />} onClick={() => setSelectedRows([])}>
                        {selectedRows.length} {t('selected')}
                    </Button>
                </>
            )}
            <div style={{ flex: 1 }} />
            {title && title()}
        </div>
    );

    const renderCardPagination = () => {
        const totalRecords = resultModel?.pagination?.totalRecords || 0;
        const pageSize = resultModel.pagination?.pageSize || 10;
        const current = resultModel.pagination?.pageNumber || 1;
        if (dataGridProps.antdTableProps?.pagination === false) return null;
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '12px 4px',
                borderTop: '1px solid #f0f0f0',
                marginTop: 8
            }}>
                <Pagination
                    size="small"
                    total={totalRecords}
                    pageSize={pageSize}
                    current={current}
                    showSizeChanger
                    pageSizeOptions={[10, 20, 50, 100]}
                    showTotal={total => `${total} kayıt`}
                    onChange={(page, size) => {
                        onFilterPageSort({
                            filter: queryModel.filter,
                            pagination: { pageNumber: page, pageSize: size },
                            orderByItems: queryModel.orderByItems
                        });
                    }}
                />
            </div>
        );
    };

    if (useCardView) {
        const data = resultModel.data || [];
        const summaryNode = typeof dataGridProps.cardSummary === 'function'
            ? dataGridProps.cardSummary()
            : null;
        return (
            <div id={`${tableId}_parent`} className="data-grid-card-view">
                {renderCardToolbar()}
                {data.length === 0 ? (
                    <Empty description={t('No Data')} style={{ padding: 32 }} />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {data.map((record, index) => {
                            const selected = isRowSelected(record);
                            const customClassName = typeof dataGridProps.rowClassName === 'function'
                                ? dataGridProps.rowClassName(record, index)
                                : '';
                            const onRowProps = typeof dataGridProps.onRow === 'function'
                                ? dataGridProps.onRow(record, index) || {}
                                : {};
                            const cardContent = dataGridProps.cardRender(record, {
                                index,
                                isSelected: selected,
                                toggleSelect: () => toggleRowSelect(record),
                                columns: dataGridProps.columns,
                                refresh: refreshDataGrid
                            });
                            return (
                                <div
                                    key={record[rowKeyName] ?? index}
                                    className={`data-grid-card ${customClassName || ''} ${selected ? 'data-grid-card-selected' : ''}`}
                                    {...onRowProps}
                                    style={{
                                        background: '#fff',
                                        border: `1px solid ${selected ? '#1890ff' : '#e8e8e8'}`,
                                        borderLeft: `4px solid ${selected ? '#1890ff' : '#d9d9d9'}`,
                                        borderRadius: 6,
                                        padding: 12,
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                                        ...((onRowProps && onRowProps.style) || {})
                                    }}
                                >
                                    {dataGridProps.enableRowSelection && (
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                            <Checkbox
                                                checked={selected}
                                                onChange={() => toggleRowSelect(record)}
                                            >
                                                {selected ? t('Selected') : t('Select')}
                                            </Checkbox>
                                        </div>
                                    )}
                                    {cardContent}
                                </div>
                            );
                        })}
                    </div>
                )}
                {summaryNode && (
                    <div style={{ marginTop: 10 }}>
                        {summaryNode}
                    </div>
                )}
                {renderCardPagination()}
            </div>
        );
    }

    return (
        <div id={`${tableId}_parent`} ref={tableWrapRef}>
            <Table
                size={'small'}
                scroll={mergedScroll}
                tableLayout={virtual ? 'fixed' : callerLayout}
                rowKey={dataGridProps.rowKey || 'id'}
                columns={virtualColumns}
                rowSelection={
                    dataGridProps.enableRowSelection
                        // Explicit width so the colgroup is fully specified: with a fixed
                        // layout, an unsized selection column makes the header and body
                        // compute widths differently and misaligns every column.
                        ? (virtual ? {...rowSelection, columnWidth: 40} : rowSelection)
                        : null
                }
                dataSource={resultModel.data}
                onChange={onChange}
                showSorterTooltip={false}
                // Virtualization replaces the tbody, so antd's expandable rows cannot
                // render. No HIS grid uses them today (every expandedRowRender is false).
                expandable={virtual ? undefined : {
                    expandedRowRender: dataGridProps.expandedRowRender,
                    rowExpandable: dataGridProps.rowExpandable,
                    //expandedRowKeys: expandedRowKeys,
                }}
                rowClassName={dataGridProps.rowClassName}
                onRow={dataGridProps.onRow}
                id={tableId}
                title={() => {
                    return (
                        <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                            <Button
                                size={'small'}
                                onClick={() => refreshDataGrid()}
                            >
                                {/*{t('Add')}*/}
                                <ReloadOutlined />
                            </Button>
                            {
                                selectedRows?.length > 0 && (
                                    <Button
                                        size={'small'}
                                        onClick={() => setSelectedRows([])}
                                    >
                                        {/*{t('Add')}*/}
                                        <CloseOutlined />
                                    </Button>
                                )
                            }
                            {title && title()}
                        </div>
                    )
                }}
                pagination={{
                    total: resultModel?.pagination?.totalRecords || 0,
                    pageSize: resultModel.pagination?.pageSize || 10,
                    size: 'small',
                    current: resultModel.pagination?.pageNumber || 1,
                    showTotal: total => `Toplam ${total} kayıt`,
                    showSizeChanger: true,
                    // simple: true,
                    hideOnSinglePage: false,
                    pageSizeOptions: [10, 20, 50, 100, 250, 500]
                }}
                {...restAntdProps}
                {...(virtual ? {components: virtualComponents} : {})}
            />
        </div>
    )
}
